
import { formatCurrency } from './utils.mjs';
import { renderAddReceiptsButton } from './addReceiptsButton.mjs';
import router from './router.mjs';
import t from 'virtual:i18n';

/**
 * 
 * @param {HTMLElement} parentEl 
 * @param {Group} group 
 */
export function renderSettlement(parentEl, group) {
    // Container
    const container = document.createElement('div');
    container.className = 'settlement-container';
    Object.assign(container.style, {
        marginTop: '30px',
        padding: '0 10px 40px 10px',
        animation: 'fadeIn 0.5s ease-out'
    });

    // Add More Receipts Button
    renderAddReceiptsButton(container, group);

    // Link/Unlink Text (Only if expenses exist)
    if (group.expenses && group.expenses.length > 0) {
        const linkContainer = document.createElement('div');
        Object.assign(linkContainer.style, {
            textAlign: 'center',
            marginBottom: '24px',
            color: '#666',
            fontSize: '14px'
        });
        linkContainer.innerHTML = `${t.somePeopleArePayingTogether}<br>`;

        const linkAnchor = document.createElement('a');
        linkAnchor.href = '#';
        linkAnchor.innerText = t.linkUnlinkThem;
        Object.assign(linkAnchor.style, {
            color: 'var(--color-primary)',
            textDecoration: 'none',
            fontWeight: '600',
            marginTop: '4px',
            display: 'inline-block'
        });

        // Open Participants modal when clicked
        linkAnchor.onclick = async (e) => {
            e.preventDefault();
            const { openParticipantsModal } = await import('./main.mjs');
            openParticipantsModal(group);
        };

        linkContainer.appendChild(linkAnchor);
        container.appendChild(linkContainer);
    }

    // Settlements List
    const settlementList = document.createElement('div');
    settlementList.className = 'settlement-list';

    renderSettlementCards(settlementList, group);

    container.appendChild(settlementList);
    parentEl.appendChild(container);


    if (!document.getElementById('settlement-style')) {
        const style = document.createElement('style');
        style.id = 'settlement-style';
        style.innerHTML = `
            @keyframes fadeIn {
                from { opacity: 0; transform: translateY(10px); }
                to { opacity: 1; transform: translateY(0); }
            }
        `;
        document.head.appendChild(style);
    }
}

export function renderSettlementCards(settlementList, group, options = {}) {
    // Check for debtEntries
    let debtEntries = [];
    if (group.summary && group.summary.debtEntries) {
        debtEntries = group.summary.debtEntries;
    } else if (group.settlement && group.settlement.debtEntries) {
        debtEntries = group.settlement.debtEntries;
    }

    if (debtEntries.length === 0) {
        // Only show "All squared away!" if there are expenses
        if (group.expenses && group.expenses.length > 0 && !options.isModal) {
            const emptyState = document.createElement('div');
            emptyState.innerText = t.allSquaredAway;
            Object.assign(emptyState.style, {
                textAlign: 'center',
                color: '#aaa',
                fontStyle: 'italic',
                padding: '20px'
            });
            settlementList.appendChild(emptyState);
        }
    } else {
        if (options.isModal) {
            const title = document.createElement('div');
            title.innerText = t.debtDetails;
            Object.assign(title.style, {
                fontSize: '16px',
                fontWeight: '600',
                color: '#1a1a2e',
                marginBottom: '12px',
                marginTop: '20px',
                textAlign: 'center',
            });
            settlementList.appendChild(title);
        }

        debtEntries.forEach(entry => {
            const card = document.createElement('div');
            card.className = 'settlement-card';

            const baseStyles = {
                backgroundColor: '#ffffff',
                borderRadius: '16px',
                padding: '16px 20px',
                marginBottom: '12px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                border: '1px solid #f0f0f0',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s'
            };

            if (options.isModal) {
                // Swap styling a bit to look better on modal
                Object.assign(baseStyles, {
                    backgroundColor: '#f8f9fa',
                    boxShadow: 'none',
                    border: '1px solid #e0e0e0',
                    borderRadius: '12px',
                    padding: '12px 16px'
                });
            }

            Object.assign(card.style, baseStyles);

            card.onmouseover = () => {
                if (!options.isModal) {
                    card.style.transform = 'translateY(-2px)';
                    card.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.1)';
                } else {
                    card.style.backgroundColor = '#f0f0f0';
                }
            };
            card.onmouseout = () => {
                if (!options.isModal) {
                    card.style.transform = 'translateY(0)';
                    card.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.08)';
                } else {
                    card.style.backgroundColor = '#f8f9fa';
                }
            };

            const debtorName = getParticipantName(group, entry.debtor);
            const creditorName = getParticipantName(group, entry.creditor);

            // Left: payer names stacked (debtor → creditor)
            const leftSide = document.createElement('div');
            Object.assign(leftSide.style, {
                display: 'flex',
                flexDirection: 'column',
                flex: '1',
                minWidth: '0',
            });

            const debtorDiv = document.createElement('div');
            debtorDiv.innerHTML = `<span style="color: #333; font-weight: 500;">${debtorName}</span> <span style="color: #888; font-size:13px;">&rarr;</span>`;
            debtorDiv.style.fontSize = options.isModal ? '14px' : '15px';

            const creditorDiv = document.createElement('div');
            creditorDiv.innerHTML = `<span style="color: #333; font-weight: 500;">${creditorName}</span>`;
            creditorDiv.style.fontSize = options.isModal ? '14px' : '15px';

            leftSide.appendChild(debtorDiv);
            leftSide.appendChild(creditorDiv);

            // Right: amount (prominent) + info icon
            const rightSide = document.createElement('div');
            Object.assign(rightSide.style, {
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                flexShrink: '0',
                marginLeft: '12px',
            });

            if (entry.amount > 0) {
                const amountEl = document.createElement('span');
                amountEl.innerText = formatCurrency(entry.amount, group.currency);
                Object.assign(amountEl.style, {
                    fontWeight: '700',
                    fontSize: options.isModal ? '15px' : '16px',
                    color: options.isModal ? 'var(--color-primary)' : '#1a1a2e',
                    whiteSpace: 'nowrap',
                });
                rightSide.appendChild(amountEl);
                if (!options.isModal) {
                    rightSide.innerHTML += `<svg style="width:22px;height:22px;fill:#aaa;flex-shrink:0;" viewBox="0 0 24 24"><path d="M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/></svg>`;
                } else {
                    rightSide.innerHTML += `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0; opacity: 0.8;"><polyline points="9 18 15 12 9 6"></polyline></svg>`;
                }
            } else {
                rightSide.innerHTML += `<svg style="width:22px;height:22px;fill:#00897B;flex-shrink:0;" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>`;
            }

            card.appendChild(leftSide);
            card.appendChild(rightSide);

            card.onclick = () => {
                if (options.isModal && options.onCardClick) {
                    options.onCardClick(entry);
                } else {
                    router.navigate(`/home/${group.id}/payback/${entry.creditor}/${entry.debtor}`);
                }
            };

            settlementList.appendChild(card);
        });

        // "Transactions minimized" helper label
        if (group.groupSettings?.simplify === true && !options.isModal) {
            const minimizedLabel = document.createElement('div');
            minimizedLabel.innerText = t.transactionsMinimized;
            Object.assign(minimizedLabel.style, {
                textAlign: 'center',
                fontSize: '12px',
                color: '#aaa',
                marginTop: '4px',
                marginBottom: '12px',
                fontStyle: 'italic',
            });
            settlementList.appendChild(minimizedLabel);
        }
    }
}

function getParticipantName(group, index) {
    if (group.settlement && group.settlement.linkedParticipantGroups && group.settlement.linkedParticipantGroups[index]) {
        const linkedIds = group.settlement.linkedParticipantGroups[index].participants;
        return linkedIds.map(pId => group.participants[pId]).join(' & ');
    }
    return group.participants[index] || 'Unknown';
}
